import { CONTENT_TYPE } from "~constants";
import {
    type Image as HydratedImage,
    type Video as HydratedVideo,
    CuratedItemVariation,
} from "~types/types";
import type {
    Image as WardenclyffeImage,
    CuratedItem as WardenclyffeCuratedItem,
    AspectRatio as WardenclyffeAspectRatio,
    Video as WardenclyffeVideo,
    VideoStream as WardenclyffeVideoStream,
} from "~types/wardenclyffe";

import {
    HIDE_CREDIT_SECRET_STRING,
    LOOPING_VIDEO_KEYWORD,
    CAROUSEL_ITEM_TYPES,
    IMAGE_MEDIA_FORMATS,
    VIDEO_MEDIA_FORMATS,
    SQUARE_NEWSLETTER_IMAGE_PLACEMENT,
} from "./constants";
import {
    type MediaFormat,
    type HydratedMediaCarouselData,
    type HydratedSnapshotChartData,
    type HydratedEmbeddedIframeData,
    type HydratedVideoImageData,
    type HydratedVodVideoData,
    type HydratedVerticalVideoData,
    type HydratedLoopingVideoData,
    type VIDEO_VIEWPORT_ASPECT_RATIO,
    type WardenclyffeExtraAttachment,
    type HydratedMediaCarouselItem,
    type HydratedDataObject,
} from "./types";

/**
 * HYDRATION FUNCTIONS -- these functions are internal helpers
 *
 * These functions take raw objects, apply business logic and hydrate them into
 * typed lineup objects for use by our components. Separate hydration
 * methods can be used for different sources of raw objects. A Story
 * from Wardenclyffe can have a different shape than a Story fetched
 * directly from content service, for example.
 */
export function hydrateWCImage(image: WardenclyffeImage): HydratedImage {
    const { credit } = image;

    const shouldHideCredit =
        !Boolean(credit?.length) || credit === HIDE_CREDIT_SECRET_STRING;

    return {
        alt: image.alt ?? "",
        baseUrl: image.url ?? "",
        caption: image.caption ?? "",
        credit: !shouldHideCredit ? credit : "",
        origWidth: image.origWidth ?? "",
        origHeight: image.origHeight ?? "",
        placement: image.placement ?? "",
        themes: image.themes ?? [],
        type: image.type ?? "image",
    } as HydratedImage;
}

/**
 * Updated version of itemImage transform from Lineup.
 * This function takes a WardenclyffeCuratedItem, finds the
 * appropriate image then applies business logic to update
 * the image's credit if we have a credit override from
 * edit dash. We use this function for the defaultImage
 * MediaFormat, as well as image overrides for certain
 * video MediaFormat(s).
 */
export function getHydratedImageFromWardenclyffeItem(
    item: WardenclyffeCuratedItem
): HydratedImage | undefined {
    const { thumbnail } = "thumbnail" in item ? item : {};
    const { creditCurationOverride } =
        "creditCurationOverride" in item ? item : {};

    if (!isValidWCImage(thumbnail)) return undefined;

    const hydratedImage = hydrateWCImage(thumbnail as WardenclyffeImage);

    // editorial can opt to omit the credit on an
    // image entirely by passing a secret string from
    // edit dash at the item level.
    if (creditCurationOverride === HIDE_CREDIT_SECRET_STRING) {
        return stripUndefinedKeys({
            ...hydratedImage,
            credit: undefined,
        });
    }

    return stripUndefinedKeys({
        ...hydratedImage,
        credit: getCreditFromCuratedItem(item),
    });
}

export function formatCaptionForVideo(caption?: string): string | undefined {
    if (!caption) return undefined;

    let formattedCaption = caption.replace(/^(\w)?Watch: /i, "");
    formattedCaption = formattedCaption.replace(/^(\w)?Listen: /i, "");
    formattedCaption = formattedCaption.replace(/\(Audio\)/i, "");
    formattedCaption = formattedCaption.replace(/\(video\)/i, "");
    formattedCaption = formattedCaption.replace(/\(podcast\)/i, "");
    formattedCaption = formattedCaption.replace(/\s+/g, " ");

    return formattedCaption;
}

export function getCreditFromCuratedItem(
    item: WardenclyffeCuratedItem
): string | undefined {
    const { thumbnail } = "thumbnail" in item ? item : {};
    const { lede } = "lede" in item ? item : {};
    const { creditCurationOverride } =
        "creditCurationOverride" in item ? item : {};

    // Editorial can override credits on images from edit dash.
    // it's safe to return the override directly and return early.
    if (creditCurationOverride?.length) return creditCurationOverride;

    let credit = thumbnail?.credit;

    if (thumbnail?.id === lede?.id) credit = lede?.credit;

    if (!credit) return undefined;

    return prepareCreditForImage(credit);
}

/**
 * Editors and photo editors provide a list of
 * boilerplate language to be stripped from credits
 * on homepages and section fronts, this logic
 * adheres to our editorial style guide.
 */
export function prepareCreditForImage(credit?: string) {
    if (!credit?.length) return undefined;

    return credit.replace(
        /(Photographer:|Photographers:|Photo:|Source:|Courtesy:|Illustration:|Illustrator:|Video:|Photo Illustration:|Illustration by|Illustrated by|Photography by|Courtesy of) /,
        ""
    );
}

/**
 * AspectRatio is the aspect ratio of the video asset itself. (The original aspect ratio.)
 * For vertical video, we crop the asset for better display in the lineup modules.
 * VIDEO_VIEWPORT_ASPECT_RATIO is the cropped aspect ratio that we use for display,
 * as the video player accepts any arbitrary aspect ratio for sizing. For now, we have a 1 to 1
 * mapping between AspectRatio and VIDEO_VIEWPORT_ASPECT_RATIO. The original aspect ratio of the video
 * controls what the viewport aspect ratio of the resulting video placed in lineup should be.
 */
export const aspectRatioConversionMap: Record<
    WardenclyffeAspectRatio,
    VIDEO_VIEWPORT_ASPECT_RATIO
> = {
    ASPECT_1_1: "1:1",
    ASPECT_9_16: "2:3",
    ASPECT_16_9: "16:9",
};

export function hydrateWCVideo(
    video: WardenclyffeVideo,
    isVideoCurationOverride?: boolean
): HydratedVideo {
    const url =
        (video.streams as WardenclyffeVideoStream[])?.find(
            (stream) => stream.url
        )?.url ?? "";

    const thumbnail = hydrateWCImage(video.thumbnail as WardenclyffeImage);

    const aspectRatio =
        aspectRatioConversionMap[video.aspectRatio ?? "ASPECT_16_9"];

    // Editors have the ability to turn off a
    // caption for a video entirely from edit dash.
    // A secret string is provided as the video's title,
    // we use that value to remove the caption.
    const hideCaptionByRequest = video?.title === HIDE_CREDIT_SECRET_STRING;
    const updatedTitle =
        hideCaptionByRequest || !Boolean(video?.title?.length)
            ? ""
            : video.title;

    return {
        autoplay: Boolean(video?.autoplay),
        bmmrId: video.id,
        prerollAdUnit: video.prerollAdUnit ?? "",
        url,
        thumbnail,
        title: updatedTitle,
        duration: video.duration ?? 0,
        type: video.__typename,
        keywords: video.keywords ?? [],
        showName: video.showName ?? "",
        aspectRatio,
        isVertical: Boolean(aspectRatio === "2:3"),
        isLooping: isLoopingVideoOfAnyType(video),
        isVideoCurationOverride: isVideoCurationOverride ?? false,
        hideCaptionByRequest,
    } as HydratedVideo;
}

export function addCaptionToHydratedVideo(
    video: HydratedVideo,
    creditCurationOverride?: string | null
): HydratedVideo {
    const hideCaptionByRequest =
        video.hideCaptionByRequest ||
        creditCurationOverride === HIDE_CREDIT_SECRET_STRING;
    const hydratedCredit =
        !hideCaptionByRequest && Boolean(creditCurationOverride?.length)
            ? creditCurationOverride
            : undefined;

    return stripUndefinedKeys({
        ...video,
        title: formatCaptionForVideo(video?.title),
        hydratedCredit,
        hideCaptionByRequest,
    } as HydratedDataObject<HydratedVideo>);
}

export function curatedItemHasImageOverride(
    item?: WardenclyffeCuratedItem
): boolean {
    if (!item) return false;

    const { thumbnail } = "thumbnail" in item ? item : {};
    const { lede } = "lede" in item ? item : {};

    if (!thumbnail?.url?.length) return false;
    if (!lede || !("url" in lede) || !lede?.url?.length) return true;

    return thumbnail.url !== lede.url;
}

export function isLoopingVideoOfAnyType(
    video: WardenclyffeExtraAttachment | HydratedVideo
): boolean {
    const { keywords } = "keywords" in video ? video : {};

    if (!keywords) return false;

    return Boolean(keywords?.includes(LOOPING_VIDEO_KEYWORD));
}

export function isLoopingVideoExtraAttachment(
    attachment: WardenclyffeExtraAttachment
): boolean {
    const { keywords } = "keywords" in attachment ? attachment : {};

    if (!keywords) return false;

    return isLoopingVideoOfAnyType(attachment);
}

export function isImageExtraAttachment(
    attachment: WardenclyffeExtraAttachment
): boolean {
    const { url } = "url" in attachment ? attachment : {};

    return Boolean(url?.length);
}

export function isValidMediaCarouselAttachment(
    attachment: WardenclyffeExtraAttachment | null
): boolean {
    if (!attachment) return false;

    return (
        isImageExtraAttachment(attachment) ||
        isLoopingVideoExtraAttachment(attachment)
    );
}

export function isValidWCImage(image: WardenclyffeImage | undefined | null) {
    return Boolean(image?.url?.length);
}

export function isValidWCVideo(video: WardenclyffeVideo | undefined | null) {
    return Boolean(video?.id?.length);
}

export function isVideoLede(
    lede: WardenclyffeImage | WardenclyffeVideo | undefined | null
) {
    return lede?.__typename === "Video";
}

export function isVideo(item: WardenclyffeCuratedItem): boolean {
    if (!item) return false;

    const { type } = "type" in item ? item : {};

    return type === CONTENT_TYPE.video;
}

export function getValidSourceVideoFromVideosArray(
    item: WardenclyffeCuratedItem
): WardenclyffeVideo | undefined {
    const { videos } = "videos" in item ? item : {};

    if (!videos?.length) return undefined;

    const [firstValidVideo] = videos.filter(Boolean).filter(isValidWCVideo);

    if (!firstValidVideo) return undefined;

    return firstValidVideo;
}

export function isSquareImage(image: WardenclyffeImage | undefined | null) {
    return (
        isValidWCImage(image) &&
        Boolean(image?.origHeight) &&
        image?.origHeight === image?.origWidth
    );
}

export function getHydratedCarouselItems(
    attachments: WardenclyffeExtraAttachment[]
): (HydratedImage | HydratedVideo)[] {
    const hydratedMediaCarouselItems: HydratedMediaCarouselItem[] = [];

    attachments.forEach((attachment) => {
        if (isImageExtraAttachment(attachment)) {
            const hydratedImage = hydrateWCImage(
                attachment as WardenclyffeImage
            ) as HydratedImage;
            if (hydratedImage.baseUrl) {
                hydratedImage.type = CAROUSEL_ITEM_TYPES.image;
                hydratedMediaCarouselItems.push(hydratedImage);
            }
            return;
        }

        if (isLoopingVideoExtraAttachment(attachment)) {
            const hydratedVideo = hydrateWCVideo(
                attachment as WardenclyffeVideo
            ) as HydratedVideo;

            // hydrateWCVideo() identifies videos as looping
            // we can rely on the property here to filter out
            // non-looping videos.
            if (hydratedVideo?.isLooping) {
                hydratedVideo.type = CAROUSEL_ITEM_TYPES.loopingVideo;

                // EDEGE CASE: When looping videos are added to a carousel
                // in edit dash, editors have the option to edit the
                // accompanying credit. This overwritten credit
                // comes through as the attachment's title. We need to use
                // this value rather than the typical logic we use to asociate
                // captions with videos.
                hydratedVideo.title =
                    hydratedVideo.title === HIDE_CREDIT_SECRET_STRING
                        ? ""
                        : hydratedVideo?.title;

                hydratedMediaCarouselItems.push(hydratedVideo);
            }
            return;
        }
        return;
    });

    return hydratedMediaCarouselItems;
}

/**
 * Because transformation happens on the server side, and the output
 * is returned via getServerSideProps(), we cannot have any keys
 * literally defined as `undefined`. This helper function takes an object
 * and removes any keys which === `undefined`, leaving us with a valid
 * object which can be turned into JSON.
 *
 * This helper allows us to write
 * clean types where `propertyName?: string` will either be a string or
 * undefined. We don't need to default to "" and then check for the
 * string's length in the consuming component, for example.
 */
export function stripUndefinedKeys<T extends Record<string, unknown>>(
    object: T
): HydratedDataObject<T> {
    return Object.fromEntries(
        Object.entries(object).filter(([_, v]) => v !== undefined)
    ) as HydratedDataObject<T>;
}

/**
 * Get()ers
 *
 * All getter functions below will return a typed
 * object from the MediaSourceData type or `undefined` if validation
 * and/or hydration fails. This pattern should be enforced.
 * Each getter lists the associated MediaFormat in its jsdocs
 * and follows a consistent naming pattern.
 *
 * Getters are meant to take data from disparate sources,
 * validate it, apply business logic and return the ideal
 * data structure needed to render the associated MediaFormat's
 * component, or undefined.
 *
 * Important contract: If we do not have valid data for a
 * MediaFormat, it's corresponding key on CuratedItem.media
 * will be undefined, by definition.
 */

/**
 * MediaFormat.audioImage
 *
 * When podcast or audio episodes are curated into
 * a module, we use an alternate image associated with
 * the episode's show to match designs.
 * Consuming components specify if they need the AudioImage
 * explicitly. This getter validates the data, then returns
 * the appropriate HydratedImage.
 */
export function getAudioImageData(
    item: WardenclyffeCuratedItem
): HydratedImage | undefined {
    const { audios } = "audios" in item ? item : {};

    if (!audios?.length) return undefined;

    const wardenclyffeImage = audios[0]?.image;

    if (!isValidWCImage(wardenclyffeImage)) return undefined;

    return hydrateWCImage(wardenclyffeImage as WardenclyffeImage);
}

/**
 * @title MediaFormat.authorImage
 *
 * @description Certain modules require an author
 * headshot. This getter takes a WardenclyffeCuratedItem,
 * validates data, applies business logic and returns
 * a HydratedImage, or undefined.
 */
export function getAuthorImageData(
    item: WardenclyffeCuratedItem
): HydratedImage | undefined {
    // credits DO NOT EXIST on the default query for items
    // in lineup if your module requires an authorImage, you must
    // adjust your schema file to request the credits object.
    // see LineupContentOpinion2Up.graphql for an example
    const { credits } = "credits" in item ? item : {};
    const { variation } = "variation" in item ? item : {};

    // we can only render an author image if there is a
    // single author.
    if (!credits || credits.length !== 1) return undefined;

    const [author] = credits;

    // The authorImage can be turned off at the source
    // in the terminal using author.photoDisplayed, or
    // it can be turned off in edit dash using
    // item.variation. If byline is turned off, authorImage
    // is also turned off by definition.
    if (
        !author?.photoDisplayed ||
        variation === CuratedItemVariation.WithoutAuthorImage ||
        variation === CuratedItemVariation.WithoutByline ||
        variation === CuratedItemVariation.FeaturedWithoutByline ||
        variation === CuratedItemVariation.TextOverImageWithoutAuthorImage ||
        variation === CuratedItemVariation.TextOverImageWithoutByline ||
        !isValidWCImage(author?.thumbnail)
    ) {
        return undefined;
    }

    return hydrateWCImage(author.thumbnail as WardenclyffeImage);
}

/**
 * MediaFormat.embeddedIframe
 *
 * Editors have the ability to add an embedded
 * i-frame as the media component. They provide an
 * embedUrl in edit dash, which we use as the source of
 * the i-frame. We also have Toaster chart support via
 * this component, using the embedUrl.
 */
export function getEmbeddedIframeData(
    item: WardenclyffeCuratedItem
): HydratedEmbeddedIframeData | undefined {
    const { embedUrl } = "embedUrl" in item ? item : {};

    return embedUrl || undefined;
}

/**
 * MediaFormat.snapshotChart
 *
 * Editors have 4 separate fields to control snapshotCharts
 * from edit dash. Here we take those fields from the raw
 * CuratedItem, validate, then hydrate them into a single
 * typed object.
 *
 * `SnapshotDuration` and `SnapshotTicker` are required. BW - 5/15/25
 */
export function getSnapshotChartData(
    item: WardenclyffeCuratedItem
): HydratedSnapshotChartData | undefined {
    const { snapshotDuration } = "snapshotDuration" in item ? item : {};
    const { snapshotTitle } = "snapshotTitle" in item ? item : {};
    const { snapshotEndDate } = "snapshotEndDate" in item ? item : {};
    const { snapshotStartDate } = "snapshotStartDate" in item ? item : {};
    const { snapshotTicker } = "snapshotTicker" in item ? item : {};

    if (!snapshotTicker || !snapshotDuration) return undefined;

    return stripUndefinedKeys({
        snapshotDuration,
        snapshotTitle: snapshotTitle?.length ? snapshotTitle : undefined,
        snapshotEndDate: snapshotEndDate?.length ? snapshotEndDate : undefined,
        snapshotStartDate: snapshotStartDate?.length
            ? snapshotStartDate
            : undefined,
        snapshotTicker,
    });
}

/**
 * MediaFormat.carousel
 *
 * Editors are able to attach `n` extraAttachments to a CuratedItem
 * in edit dash. This function validates, filters, then hydrates the data
 * returning an array of hydrated videos and images or undefined if no
 * valid data exists.
 */
export function getCarouselData(
    item: WardenclyffeCuratedItem
): HydratedMediaCarouselData | undefined {
    const { extraAttachments } = "extraAttachments" in item ? item : {};

    const validExtraAttachments = extraAttachments
        ?.filter(Boolean)
        .filter(isValidMediaCarouselAttachment);

    if (!validExtraAttachments?.length) return undefined;

    const hydratedCarouselItems = getHydratedCarouselItems(
        validExtraAttachments as (WardenclyffeVideo | WardenclyffeImage)[]
    );

    return {
        hydratedCarouselItems,
        numberOfItems: hydratedCarouselItems.length,
    };
}

/**
 * MediaFormat.optOutMedia
 *
 * This allows the editors to say an item should not show an
 * accomponying media component, even if the module supports
 * media in this slot. Editors adjust editorial weight of
 * stories, hide repetitive images and balance spacing with
 * this feature. We want to return undefined here so the key is
 * stripped from the media object if the value is false. This keeps
 * in line with our consumption pattern in the Media package. If we
 * cannot return valid data to render the corresponding component, we
 * omit that key entirely from the returned HydratedMediaSourceData.
 */
export function getItemOptOutDecision(
    item: WardenclyffeCuratedItem
): boolean | undefined {
    return "variation" in item &&
        item?.variation === CuratedItemVariation.Imageless
        ? true
        : undefined;
}

/**
 * MediaFormat.magazineCoverImage
 *
 * The magazine cover image is extracted from a
 * CuratedItem's magazineIssue property, hydrated and set as
 * media.magazineCoverImage for use by the <Media> component.
 * Using this format is a design decision made at the module level,
 * it is not an explicit choice by editors in edit dash. Only
 * specific content has the magazineIssue property.
 */
export function getMagazineCoverImageData(
    item: WardenclyffeCuratedItem
): HydratedImage | undefined {
    const { coverImage } =
        ("magazineIssue" in item && item?.magazineIssue) || {};

    if (!isValidWCImage(coverImage)) return undefined;

    return hydrateWCImage(coverImage as WardenclyffeImage);
}

/**
 * MediaFormat.newsletterImage
 *
 * For newsletters design can signify they'd like to use
 * an image associated with the newsletter rather than the
 * campaign. This image is square and fits into Cards
 * and other treatments. Editors have the ability to override
 * the default newsletter image, but they must upload a square
 * image to work in these placements.
 */
export function getNewsletterImageData(
    item: WardenclyffeCuratedItem
): HydratedImage | undefined {
    const { newsletter, thumbnail } = ("newsletter" in item && item) || {};

    const curatedImageOverride =
        newsletter && isValidWCImage(thumbnail) && isSquareImage(thumbnail);

    if (curatedImageOverride) {
        return hydrateWCImage(thumbnail as WardenclyffeImage);
    }

    const squareNewsletterImage = newsletter?.images?.find((image) =>
        image?.placement?.includes(SQUARE_NEWSLETTER_IMAGE_PLACEMENT)
    );

    if (!isValidWCImage(squareNewsletterImage)) return undefined;

    return hydrateWCImage(squareNewsletterImage as WardenclyffeImage);
}

/**
 * @title MediaFormat.videoImage
 *
 * @description If the source item is a Video, we use the video's thumbnail
 * as the item's image. Editors also have the ability to provide
 * an alternate image via edit dash, which we prioritize if it's
 * provided.
 */
export function getVideoImageData(
    item: WardenclyffeCuratedItem
): HydratedVideoImageData | undefined {
    const { videoCurationOverride, creditCurationOverride } =
        "videoCurationOverride" in item ? item : {};
    const { lede } = "lede" in item ? item : {};
    const isVideoItem = isVideo(item);
    const validVideoFromVideosArray = getValidSourceVideoFromVideosArray(item);
    const hasValidVideoFromVideoCurationOverride =
        videoCurationOverride && isValidWCVideo(videoCurationOverride);
    const hasValidVideoFromLede =
        isVideoItem &&
        isVideoLede(lede) &&
        isValidWCVideo(lede as WardenclyffeVideo);
    const hasValidVideoFromVideosArray =
        isVideoItem && Boolean(validVideoFromVideosArray);

    let sourceVideo;

    if (hasValidVideoFromVideoCurationOverride) {
        sourceVideo = videoCurationOverride as WardenclyffeVideo;
    } else if (hasValidVideoFromLede) {
        sourceVideo = lede as WardenclyffeVideo;
    } else if (hasValidVideoFromVideosArray) {
        sourceVideo = validVideoFromVideosArray as WardenclyffeVideo;
    }

    if (!sourceVideo) return undefined;

    const hydratedVideo = addCaptionToHydratedVideo(
        hydrateWCVideo(sourceVideo),
        creditCurationOverride
    );

    const {
        thumbnail: videoThumbnail,
        showName,
        duration,
    } = (hydratedVideo as HydratedVideo) || {};

    const itemImage = getHydratedImageFromWardenclyffeItem(item);

    if (!videoThumbnail && !itemImage) return undefined;

    return stripUndefinedKeys({
        image: itemImage || videoThumbnail,
        videoDuration: duration ?? 0,
        showName: showName?.length ? showName : undefined,
    });
}

/**
 * MediaFormat.defaultImage
 *
 * We use the item's homepage image from Wolf to power this component
 * if no override is provided by editors in edit dash. If editors add
 * an override, it comes through as item.thumbnail. A transform in
 * Wardenclyffe handles the logic as to which image is the
 * thumbnail and we use that image.
 */
export function getDefaultImageData(item: WardenclyffeCuratedItem) {
    return getHydratedImageFromWardenclyffeItem(item);
}

/**
 * MediaFormat.vodVideo
 *
 * Editors can curate VOD (video on demand) Videos into lineup modules.
 * On our side we find the correct video, confirm it is not a vertical
 * video (because vertical videos are a distinct Media Format with
 * unique designs, not all modules suppport vertical videos. We have
 * to remove them from consideration as simple VOD videos), then hydrate
 * and return the video for use by the VideoMedia component.
 */
export function getVodVideoData(
    item: WardenclyffeCuratedItem
): HydratedVodVideoData | undefined {
    const { videoCurationOverride } =
        "videoCurationOverride" in item ? item : {};
    const { lede } = "lede" in item ? item : {};
    const { creditCurationOverride } =
        "creditCurationOverride" in item ? item : {};
    const isVideoItem = isVideo(item);
    const validVideoFromVideosArray = getValidSourceVideoFromVideosArray(item);
    const hasValidVideoFromVideoCurationOverride =
        videoCurationOverride && isValidWCVideo(videoCurationOverride);
    const hasValidVideoFromLede =
        isVideoItem &&
        isVideoLede(lede) &&
        isValidWCVideo(lede as WardenclyffeVideo);
    const hasValidVideoFromVideosArray =
        isVideoItem && Boolean(validVideoFromVideosArray);

    let sourceVideo;

    if (hasValidVideoFromVideoCurationOverride) {
        sourceVideo = hydrateWCVideo(
            videoCurationOverride as WardenclyffeVideo,
            true
        );
    } else if (hasValidVideoFromLede) {
        sourceVideo = hydrateWCVideo(lede as WardenclyffeVideo);
    } else if (hasValidVideoFromVideosArray) {
        sourceVideo = hydrateWCVideo(
            validVideoFromVideosArray as WardenclyffeVideo
        );
    }

    // Vertical Videos and Looping videos should not qualify
    // for MediaFormat.vodVideo as they have distinct
    // formats with specific treatments.
    if (!sourceVideo || sourceVideo.isVertical || sourceVideo.isLooping) {
        return undefined;
    }

    // extra caption logic is required from vodVideo
    sourceVideo = addCaptionToHydratedVideo(
        sourceVideo,
        creditCurationOverride
    );

    return stripUndefinedKeys({
        video: sourceVideo as HydratedVideo,
        image: getHydratedImageFromWardenclyffeItem(item),
    });
}

/**
 * MediaFormat.livestreamVideo
 *
 * Editors can curate Live streaming video, `livestream` into
 * an item as its media component. They select from a dropdown of
 * predefined Streams, we pass the provided string into the
 * video player.
 */
export function getLivestreamVideoData(
    item: WardenclyffeCuratedItem
): string | undefined {
    const { livestream } = "livestream" in item ? item : {};

    if (!livestream?.length) return undefined;

    return livestream;
}

/**
 * MediaFormat.verticalVideo
 *
 * Editors can curate vertical Videos into lineup modules
 * on our side we need to be able to determine:
 *   a: if the WardenclyffeCuratedItem is itself a vertical video
 *   b: if not, has an editor added a vertical video as
 *      a decoration, or extra media type.
 *   c: should we use the video thumbnail or item's image
 *      as the poster.
 *   d: Do not render if the video is a vertical video.
 * This getter validates then hydrates the data and returns
 * a normalized HydratedVerticalVideoData object.
 */
export function getVerticalVideoData(
    item: WardenclyffeCuratedItem
): HydratedVerticalVideoData | undefined {
    const { videoCurationOverride } =
        "videoCurationOverride" in item ? item : {};
    const { lede } = "lede" in item ? item : {};
    const { creditCurationOverride } =
        "creditCurationOverride" in item ? item : {};
    const { variation } = "variation" in item ? item : {};
    const isVideoItem = isVideo(item);
    const validVideoFromVideosArray = getValidSourceVideoFromVideosArray(item);
    const hasValidVideoFromVideoCurationOverride = isValidWCVideo(
        videoCurationOverride
    );
    const hasValidVideoFromLede =
        isVideoItem &&
        isVideoLede(lede) &&
        isValidWCVideo(lede as WardenclyffeVideo);
    const hasValidVideoFromVideosArray =
        isVideoItem && Boolean(validVideoFromVideosArray);
    const useSmallPlayer =
        variation === CuratedItemVariation.SmallVerticalVideoPlayer;

    let sourceVideo;

    if (hasValidVideoFromVideoCurationOverride) {
        sourceVideo = hydrateWCVideo(
            videoCurationOverride as WardenclyffeVideo,
            true
        );
    } else if (hasValidVideoFromLede) {
        sourceVideo = hydrateWCVideo(lede as WardenclyffeVideo);
    } else if (hasValidVideoFromVideosArray) {
        sourceVideo = hydrateWCVideo(
            validVideoFromVideosArray as WardenclyffeVideo
        );
    }

    if (!sourceVideo || !sourceVideo.isVertical || sourceVideo.isLooping) {
        return undefined;
    }

    sourceVideo = addCaptionToHydratedVideo(
        sourceVideo,
        creditCurationOverride
    );

    const { thumbnail } = sourceVideo;
    const itemImage = getHydratedImageFromWardenclyffeItem(item);
    const hasCuratedImageOverride = curatedItemHasImageOverride(item);

    let image;

    // Notes from Video team:
    // There are 4 scenerios the following supports for vertical video thumbnail overrides:
    // 1) regular curated vertical video without override (use vertical thumbnail default)
    if (isVideoItem && !hasCuratedImageOverride) {
        image = thumbnail;
    }

    // 2) regular curated vertical video with override (use image override)
    else if (isVideoItem && hasCuratedImageOverride) {
        image = itemImage;
    }

    // 3) other story types, with vertical videos, without image override (use vertical thumbnail default)
    else if (!isVideoItem && !hasCuratedImageOverride) {
        image = thumbnail;
    }

    // 4) other story types, with vertical videos, with image override (use image override)
    //    NOTE: Interactive content type will always fall into this category if a vertical
    //    video is added as a decoration because Interactives do not have ledes.
    else if (!isVideoItem && hasCuratedImageOverride) {
        image = itemImage;
    }

    return stripUndefinedKeys({
        video: sourceVideo,
        image,
        isVideoCurationOverride: hasValidVideoFromVideoCurationOverride,
        useSmallPlayer,
    });
}

/**
 * MediaFormat.loopingVideo
 *
 * Editors can curate loopingVideos (aka Ambient Video), which
 * render as autoplaying vidoes without controls and no audio.
 *
 */
export function getLoopingVideoData(
    item: WardenclyffeCuratedItem
): HydratedLoopingVideoData | undefined {
    // looping video can only come from videoCurationOverride
    const { videoCurationOverride } =
        "videoCurationOverride" in item ? item : {};
    const { creditCurationOverride } =
        "creditCurationOverride" in item ? item : {};

    if (
        !videoCurationOverride ||
        !isValidWCVideo(videoCurationOverride) ||
        !isLoopingVideoOfAnyType(videoCurationOverride)
    ) {
        return undefined;
    }

    const hydratedVideo = hydrateWCVideo(videoCurationOverride, true);
    const videoWithCaption = addCaptionToHydratedVideo(
        hydratedVideo,
        creditCurationOverride
    );

    return {
        video: videoWithCaption,
    };
}

export function isImageMediaFormat(format: MediaFormat): boolean {
    return IMAGE_MEDIA_FORMATS.has(format);
}

export function isVideoMediaFormat(format: MediaFormat): boolean {
    return VIDEO_MEDIA_FORMATS.has(format);
}
